import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Survey, SurveyAnswer } from '../services/survey/types';
import { useSurveyService } from '../services/survey';
import { ResponseStatus } from '../services/common/types';
import { readStoredAnswers, writeStoredAnswers, readStoredActiveSurveyId, writeStoredActiveSurveyId, clearStoredActiveSurveyId } from './session';

interface SurveyContextType {
    surveys: Survey[];
    activeSurvey: Survey | null;
    setActiveSurvey: (survey: Survey | null) => void;
    answers: Record<string, SurveyAnswer[]>;
    setAnswer: (surveyId: string, answer: SurveyAnswer) => void;
    completedIds: Set<string>;
    submitSurvey: (surveyId: string) => Promise<boolean>;
    loading: boolean;
}

const SurveyContext = createContext<SurveyContextType>({
    surveys: [],
    activeSurvey: null,
    setActiveSurvey: () => {},
    answers: {},
    setAnswer: () => {},
    completedIds: new Set(),
    submitSurvey: async () => false,
    loading: false,
});

const SurveyContextProvider = ( { children }: { children: React.ReactNode }) => {
    const service = useSurveyService();
    const [surveys, setSurveys] = useState<Survey[]>([]);
    const [activeSurvey, setActiveSurvey] = useState<Survey | null>(null);
    const [answers, setAnswers] = useState<Record<string, SurveyAnswer[]>>(readStoredAnswers);
    const [completedIds, setCompletedIds] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        service.searchSurveys().then(data => {
            const list = data && data.length > 0 ? [data[0]] : [];
            setSurveys(list);
            setLoading(false);

            const savedActiveSurveyId = readStoredActiveSurveyId();
            if (savedActiveSurveyId) {
                const match = list.find(s => s.id === savedActiveSurveyId);
                if (match) {
                    setActiveSurvey(match);
                } else {
                    clearStoredActiveSurveyId();
                }
            }
        });
    }, [service]);

    useEffect(() => {
        writeStoredAnswers(answers);
    }, [answers]);

    useEffect(() => {
        if (activeSurvey) {
            writeStoredActiveSurveyId(activeSurvey.id);
        } else {
            clearStoredActiveSurveyId();
        }
    }, [activeSurvey]);

    const setAnswer = useCallback((surveyId: string, answer: SurveyAnswer) => {
        setAnswers(prev => {
            const existing = prev[surveyId] ?? [];
            const updated = existing.filter(a => a.questionId !== answer.questionId);
            return { ...prev, [surveyId]: [...updated, answer] };
        });
    }, []);

    const submitSurvey = useCallback(async (surveyId: string): Promise<boolean> => {
        const surveyAnswers = answers[surveyId] ?? [];
        try {
            const data = await service.submitSurvey({ surveyId, answers: surveyAnswers });

            if (data.status === ResponseStatus.ERROR) {
                return false;
            }

            setCompletedIds(prev => new Set(prev).add(surveyId));
            setAnswers(prev => {
                const rest = { ...prev };
                delete rest[surveyId];
                return rest;
            });
            return true;
        } catch {
            return false;
        }
    }, [answers, service]);

    const value = useMemo(() => ({
        surveys,
        activeSurvey,
        setActiveSurvey,
        answers,
        setAnswer,
        completedIds,
        submitSurvey,
        loading,
    }), [surveys, activeSurvey, answers, setAnswer, completedIds, submitSurvey, loading]);

    return (
        <SurveyContext.Provider value={value}>
            {children}
        </SurveyContext.Provider>
    );
};

const useSurvey = () => useContext(SurveyContext);

export { useSurvey, SurveyContextProvider };